'use client';

import { useEffect, useRef, useState } from 'react';

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    const words = paragraph.split(' ');
    let current = '';
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    lines.push(current);
  }
  return lines;
}

export default function BrowserInstagramPost() {
  const [username, setUsername] = useState('cleanor.app');
  const [caption, setCaption] = useState(
    'Built a tool that runs entirely in your browser. No servers, no tracking.',
  );
  const [likes, setLikes] = useState('1,024');
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  function handleFile(file: File | undefined) {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setImage(img);
    img.src = url;
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const scale = 2;
    const w = 540;
    const header = 60;
    const imageH = 540;
    const pad = 16;

    ctx.font = '400 16px system-ui, sans-serif';
    const captionLines = wrap(ctx, `${username} ${caption}`, w - pad * 2);
    const footer = 70 + captionLines.length * 22 + pad;
    const h = header + imageH + footer;

    canvas.width = w * scale;
    canvas.height = h * scale;
    ctx.scale(scale, scale);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);

    // header: avatar + username
    ctx.beginPath();
    ctx.arc(pad + 18, header / 2, 18, 0, Math.PI * 2);
    ctx.fillStyle = '#dd2a7b';
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = '600 18px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText((username.trim()[0] || '?').toUpperCase(), pad + 18, header / 2 + 1);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#000000';
    ctx.font = '600 15px system-ui, sans-serif';
    ctx.fillText(username.trim() || 'username', pad + 46, header / 2 + 5);

    // image area
    if (image) {
      const ratio = Math.max(w / image.naturalWidth, imageH / image.naturalHeight);
      const iw = image.naturalWidth * ratio;
      const ih = image.naturalHeight * ratio;
      ctx.drawImage(image, (w - iw) / 2, header + (imageH - ih) / 2, iw, ih);
    } else {
      const grad = ctx.createLinearGradient(0, header, w, header + imageH);
      grad.addColorStop(0, '#feda75');
      grad.addColorStop(0.5, '#d62976');
      grad.addColorStop(1, '#4f5bd5');
      ctx.fillStyle = grad;
      ctx.fillRect(0, header, w, imageH);
    }

    // footer
    let fy = header + imageH + 34;
    ctx.fillStyle = '#000000';
    ctx.font = '600 15px system-ui, sans-serif';
    ctx.fillText(`${likes.trim() || '0'} likes`, pad, fy);
    fy += 26;
    ctx.font = '400 15px system-ui, sans-serif';
    for (const line of captionLines) {
      ctx.fillText(line, pad, fy);
      fy += 22;
    }

    canvas.toBlob((blob) => {
      if (blob) setDownloadUrl(URL.createObjectURL(blob));
    }, 'image/png');
  }, [username, caption, likes, image]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Instagram</span>
        <h2 className="tool-live-title">Instagram post generator</h2>
        <p className="tool-live-subtitle">
          Design a mock Instagram post and download it as an image. Rendered in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Username</span>
              <input onChange={(e) => setUsername(e.target.value)} type="text" value={username} />
            </label>
            <label className="tool-live-field">
              <span>Likes</span>
              <input onChange={(e) => setLikes(e.target.value)} type="text" value={likes} />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Caption</span>
              <textarea onChange={(e) => setCaption(e.target.value)} rows={2} value={caption} />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Post image (optional)</span>
              <input
                accept="image/*"
                onChange={(e) => handleFile(e.target.files?.[0])}
                type="file"
              />
            </label>
          </div>
          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download="instagram-post.png"
                href={downloadUrl}
              >
                Download PNG
              </a>
            ) : null}
          </div>
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          />
          <p className="tool-live-panel-note">
            Rendered locally with an HTML canvas. This is a mockup for design and fun.
          </p>
        </section>
      </div>
    </div>
  );
}
